import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router';

import { paths } from '@/config/paths';
import { useCanSchedule } from '@/features/scheduling/hooks/useCanSchedule';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils';

import homeFilledIcon from '@/assets/custom-icons/home-filled.svg';
import homeIcon from '@/assets/custom-icons/home.svg';
import progressFilledIcon from '@/assets/custom-icons/progress-filled.svg';
import progressIcon from '@/assets/custom-icons/progress.svg';
import sessionsFilledIcon from '@/assets/custom-icons/sessions-filled.svg';
import sessionsIcon from '@/assets/custom-icons/sessions.svg';
import settingsFilledIcon from '@/assets/custom-icons/settings-filled.svg';
import settingsIcon from '@/assets/custom-icons/settings.svg';
import { MOBILE_NAV_ID } from '@/components/layouts/AppLayout/AppLayout';

interface NavItem {
  label: string;
  href: string;
  iconSrc: string;
  iconFilledSrc: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const generateNavItems = (
  t: TFunction<'components', 'appLayout.navigation'>,
  isSchedulingEnabled: boolean,
  hasExternalScheduling: boolean,
): NavItem[] => [
  {
    label: t('home'),
    href: paths.home.getHref(),
    iconSrc: homeIcon,
    iconFilledSrc: homeFilledIcon,
  },
  ...(isSchedulingEnabled && !hasExternalScheduling
    ? [
        {
          label: t('sessions'),
          href: paths.scheduling.sessions.home.getHref(),
          iconSrc: sessionsIcon,
          iconFilledSrc: sessionsFilledIcon,
        },
      ]
    : []),
  {
    label: t('progressCenter'),
    href: paths.progressCenter.home.getHref(),
    iconSrc: progressIcon,
    iconFilledSrc: progressFilledIcon,
  },
  {
    label: t('managementPanel'),
    href: paths.managementPanel.home.getHref(),
    iconSrc: settingsIcon,
    iconFilledSrc: settingsFilledIcon,
  },
];

export const APP_NAVBAR_HEIGHT_IN_PX = 72;

type NavbarItemProps = NavItem;

const NavbarItem = ({ iconSrc, iconFilledSrc, label, href }: NavbarItemProps) => {
  const isActive = useMatch(href);

  return (
    <li>
      <Link to={href} className={cn('flex size-11 items-center justify-center')} aria-label={label} aria-current={isActive ? 'page' : undefined}>
        <img src={isActive ? iconFilledSrc : iconSrc} alt='' className='size-6' aria-hidden />
      </Link>
    </li>
  );
};

export const AppNavbar = () => {
  const { t } = useTranslation('components', { keyPrefix: 'appLayout.navigation' });

  const { userData } = useAuthStore();
  const hasExternalScheduling = !!userData?.center?.externalSchedulingUrl;
  const { canSchedule } = useCanSchedule();
  const isSchedulingEnabled = canSchedule;

  const navItems = generateNavItems(t, isSchedulingEnabled, hasExternalScheduling);

  return (
    <nav
      id={MOBILE_NAV_ID}
      aria-label={t('ariaLabel')}
      className='bg-fill-background-neutral border-outline-neutral fixed bottom-0 left-0 z-10 flex w-full border-t px-8 pt-3 pb-4'
      style={{
        height: `${APP_NAVBAR_HEIGHT_IN_PX}px`,
      }}
    >
      <ul className='flex w-full items-center justify-around gap-2'>
        {navItems?.map(({ label, href, iconSrc, iconFilledSrc }) => (
          <NavbarItem key={label} iconSrc={iconSrc} iconFilledSrc={iconFilledSrc} label={label} href={href} />
        ))}
      </ul>
    </nav>
  );
};
